import type jsPDF from 'jspdf';
import { buildCompanyForPdf } from '../../../services/pdf/companyForPdf';
import {
    asplanDateiname,
    generateAsplanPdf,
    type AsplanPdfOptionen,
} from '../../../services/pdf/generatorAsplan';
import { legeUnterlageAb, type FreigabeFesthalten, type UnterlageAblegen } from '../../../services/unterlagenAblage';
import type { ReportMetadata } from '../../../types';
import type { ProjektDokument } from '../../dokumente/types';
import type { Asplan, AsplanBundle, AsplanStatus } from '../types';
import { planAblageAngaben } from './anlagen';

/**
 * Anschluss des A+S-Moduls an die werfenden Nachbarn: jsPDF und die
 * Projektablage.
 *
 * ═══ WARUM ES NUR EINEN ABLAGEWEG GIBT ════════════════════════════════════
 * Geschrieben wird ausschliesslich ueber `legeUnterlageAb` — den zentralen
 * Ablageweg aller erzeugten Unterlagen (Plan Stufe 1A.2). Er kennzeichnet die
 * Zeile als vom Modul `asplan` erzeugt (`erzeugt_aus`, `fassung_ref`) und
 * legt sie in die Kategorie der eigenen Unterlagen; darunter liegt weiterhin
 * `uploadDokument` mit seiner festgenagelten Reihenfolge — erst die Datei in
 * den Bucket, dann die Zeile, und scheitert die Zeile, wird die Datei wieder
 * abgeraeumt. Ein zweiter Ablageweg neben diesem haette dieselbe Reihenfolge
 * nachbauen muessen und waere beim naechsten Policy-Wechsel stehengeblieben.
 *
 * Der Abgleich mit der Anlagenliste (`anlagen.ts`, `AblageKarte`) haengt an
 * `module` und `nur_admin`, nicht an Kategorie oder Ordner — er sieht neue
 * und alte Fassungen gleich. Der Altbestand (Kategorie „Plaene", Ordner
 * `A+S-Plan`) wird im SQL nachgekennzeichnet; `planAblageEntwurf` bleibt als
 * sein Fingerabdruck stehen.
 *
 * ═══ WARUM EINE NEUE FASSUNG DIE ALTE NICHT ERSETZT ═══════════════════════
 * Die Projektablage ueberschreibt grundsaetzlich nichts (`upsert: false` auf
 * einen frischen UUID-Pfad, kein update auf dem Bucket). Jede abgelegte
 * Fassung ist eine eigene Zeile mit eigenem Stand. Genau das ist beim A+S-Plan
 * der Sinn der Sache: Revisionsfuehrung heisst, dass Rev. 3.0 nach der
 * Freigabe von Rev. 4.0 noch nachweisbar ist — sonst laesst sich hinterher
 * nicht mehr belegen, nach welchem Blatt gearbeitet wurde. Nur DIESELBE
 * freigegebene Fassung (gleiche `fassungRef`) kommt nicht zweimal in die
 * Liste; die Ablage gibt dann die vorhandene Zeile zurueck.
 *
 * ═══ FREIGEGEBENE FASSUNG IST AUSGABE — DER ANKER GEHOERT ZUR FREIGABE ═══
 * (Stufe 6.1/6.3) Der Steckbrief-Auszug, gegen den der Aenderungsdienst
 * spaeter vergleicht, wird IM MOMENT DER FREIGABE festgehalten
 * (`halteAsplanFreigabeFest`, Anlass `freigabe`, dieselbe Fassungskennung
 * wie die spaetere Ablage). „Plan ablegen" kann beliebig lange nach der
 * Freigabe geklickt werden; die Ablage schickt deshalb `anker: null` und
 * bildet NIE einen eigenen — ein heutiger Stand fuer eine alte Freigabe
 * verdeckte echte Abweichungen (Codex-Review 17.10.2026). Der Kern prueft nur,
 * ob zur Kennung ein Anker liegt, und meldet sonst, dass die Fassung nicht
 * ueberwacht wird. Ein Entwurf ist keine Ausgabe und bekommt keinen Anker;
 * er loest keine Rueckfrage aus. Die Projektdaten (`metadata`) gehen in den
 * Auszug der Freigabe; fehlen sie, entsteht er ohne sie — besser ein Anker
 * ohne Bauherr als keiner.
 *
 * ═══ WARUM DER ANSCHLUSS EIN ERGEBNIS ZURUECKGIBT UND NICHT WIRFT ═════════
 * Muster wie `handbuch/handbuchIntegration.ts` und
 * `planungsphase/planungIntegration.ts`: die Komponente soll nicht in einem
 * `try` stehen, und ein einzelnes kaputtes Feld darf nicht das ganze Modul
 * weissbrennen. Der Diskriminator ist eine ZEICHENKETTE und kein boolean —
 * das Projekt uebersetzt ohne `strictNullChecks`, und ein `{ ok: false }`
 * liesse sich dort versehentlich auf einem Objekt lesen, das gar keinen
 * `ok`-Schluessel hat.
 *
 * `hinweise` am Erfolg: Saetze des Kerns, die keine Fehler sind (etwa
 * „Journal-Eintrag nicht angelegt"). Der Aufrufer zeigt sie dezent an.
 */
export type Anschluss<T> =
    | { status: 'ok'; wert: T; hinweise?: string[] }
    | { status: 'fehler'; fehler: string };

const fehlertext = (e: unknown): string => {
    if (e instanceof Error) return e.message;
    if (typeof e === 'string' && e.trim()) return e;
    return 'unbekannter Fehler';
};

const alsFehler = (e: unknown, was: string): { status: 'fehler'; fehler: string } => ({
    status: 'fehler',
    fehler: `${was}: ${fehlertext(e)}`,
});

export interface PlanPdfArgs {
    bundle: AsplanBundle;
    /** Firmenstammdaten, Briefkopf- und Rechnungskonfiguration aus dem Profil. */
    company?: unknown;
    quoteConfig?: unknown;
    invoiceConfig?: unknown;
    optionen?: AsplanPdfOptionen;
}

/** Erzeugt das Plan-PDF. Getrennt vom Ablegen, damit dasselbe Blatt auch nur
 *  heruntergeladen werden kann. */
export const baueAsplanPdf = (args: PlanPdfArgs): Anschluss<jsPDF> => {
    try {
        const pdfCompany = buildCompanyForPdf(
            args.company as never, args.quoteConfig as never, args.invoiceConfig as never,
        );
        return { status: 'ok', wert: generateAsplanPdf(args.bundle, pdfCompany, args.optionen) };
    } catch (e) {
        return alsFehler(e, 'Der A+S-Plan konnte nicht erzeugt werden');
    }
};

/**
 * Der Dateiname einer Planfassung — derselbe fuer Download und Ablage.
 *
 * Er kommt aus `asplanDateiname` und wird hier NICHT nachgebaut: der
 * heruntergeladene und der abgelegte Plan muessen gleich heissen, sonst
 * liegen im Posteingang und in der Ablage zwei Dateien, denen niemand
 * ansieht, dass sie dasselbe Blatt sind.
 */
export const asplanPdfDateiname = (
    projektName: string | undefined,
    plan: { rev_label?: string | null; stand?: string | null },
): string => asplanDateiname(
    projektName || 'Projekt',
    plan.rev_label || undefined,
    plan.stand || undefined,
);

export interface AblageArgs {
    projectId: string;
    doc: jsPDF;
    projektName?: string;
    plan: {
        rev_label?: string | null;
        stand?: string | null;
        katalog_version?: string | null;
        /** Entscheidet ueber Anlass und Fassungsbezug (`planAblageAngaben`). */
        status?: AsplanStatus | null;
        freigegeben_am?: string | null;
    };
    /**
     * Nur noch der Form halber: den Hochladenden bestimmt der zentrale
     * Ablageweg selbst aus der Anmeldung. Die Felder bleiben, damit die
     * Aufrufer nicht angefasst werden muessen.
     */
    benutzerId?: string | null;
    benutzerName?: string | null;
    /**
     * Projektdaten — nur noch der Form halber an die Ausgabe gereicht: der
     * Anker liegt seit der Freigabe im Journal, die Ablage liest den
     * Steckbrief nicht (Dateikopf).
     */
    metadata?: Partial<ReportMetadata> | null;
}

/**
 * Der Auftrag an das Freigabe-Journal des Kerns (`halteFreigabeFest`) —
 * unter derselben Kennung, die `legeAsplanPdfAb` der freigegebenen Fassung
 * gibt (`planAblageAngaben`). Zu bilden unmittelbar nach `freigebenAsplan`,
 * aus dem FREIGEGEBENEN Plan; rein, damit das Modul ihn fuer „Erneut
 * festhalten" behalten kann.
 */
export const asplanFreigabeAuftrag = (
    plan: Pick<Asplan, 'project_id' | 'rev_label' | 'stand' | 'katalog_version' | 'status' | 'freigegeben_am'>,
    metadata: Partial<ReportMetadata> | null,
): FreigabeFesthalten => {
    const angaben = planAblageAngaben({
        revLabel: plan.rev_label,
        standIso: plan.stand,
        katalogVersion: plan.katalog_version,
        status: plan.status,
        freigegebenAm: plan.freigegeben_am,
    });
    return { projectId: plan.project_id, unterlage: 'asplan', fassungRefs: [angaben.fassungRef], metadata };
};

/**
 * Legt eine erzeugte Planfassung in der Projektablage ab.
 *
 * Die Bytes entstehen ueber `doc.output('blob')` — dasselbe Muster wie
 * `pdfAlsBlob` im Handbuch. `legeUnterlageAb` nimmt den Blob entgegen und
 * liest Groesse und Inhaltstyp daran ab.
 *
 * `schon_abgelegt` ist kein Fehler: dieselbe freigegebene Fassung lag schon
 * in der Ablage, und zurueck kommt genau diese Zeile. Fuer den Aufrufer ist
 * das ein Erfolg — das Blatt liegt dort, wo es liegen soll.
 */
export const legeAsplanPdfAb = async (args: AblageArgs): Promise<Anschluss<ProjektDokument>> => {
    let blob: Blob;
    try {
        blob = args.doc.output('blob') as Blob;
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht ausgegeben werden');
    }

    const angaben = planAblageAngaben({
        revLabel: args.plan.rev_label,
        standIso: args.plan.stand,
        katalogVersion: args.plan.katalog_version,
        status: args.plan.status,
        freigegebenAm: args.plan.freigegeben_am,
    });

    const auftrag: UnterlageAblegen = {
        projectId: args.projectId,
        modul: 'asplan',
        datei: blob,
        dateiname: asplanPdfDateiname(args.projektName, args.plan),
        titel: angaben.titel,
        stand: angaben.stand,
        fassungRef: angaben.fassungRef,
        anlass: angaben.anlass,
        beschreibung: angaben.beschreibung,
    };
    // Nur die freigegebene Fassung ist eine Ausgabe — ohne eigenen Anker: der
    // liegt seit der Freigabe im Journal (siehe Dateikopf).
    if (angaben.anlass === 'freigabe') {
        auftrag.ausgabe = { unterlage: 'asplan', metadata: args.metadata ?? null, anker: null };
    }

    try {
        const erg = await legeUnterlageAb(auftrag);
        if (erg.status === 'fehler' || !erg.dokument) {
            return alsFehler(erg.fehler, 'Der Plan konnte nicht in der Projektablage abgelegt werden');
        }
        const hinweise = (erg.hinweise || []).map(h => (h || '').trim()).filter(Boolean);
        return hinweise.length
            ? { status: 'ok', wert: erg.dokument, hinweise }
            : { status: 'ok', wert: erg.dokument };
    } catch (e) {
        // Der Ablageweg verspricht, nicht zu werfen — falls doch, bleibt das
        // Modul bedienbar und zeigt den Grund.
        return alsFehler(e, 'Der Plan konnte nicht in der Projektablage abgelegt werden');
    }
};
